import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  TextField,
  Button,
  IconButton,
  Chip,
  Card,
  CardContent,
  CardActions,
  Switch,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
  Collapse,
  Alert,
  Tooltip,
  Stack,
  Badge,
  useTheme,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  DragIndicator as DragIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Visibility as PreviewIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckIcon,
  Image as ImageIcon,
  TextFields as TextIcon,
  RadioButtonChecked as RadioIcon,
  CheckBox as CheckboxIcon,
  CalendarToday as DateIcon,
  Numbers as NumberIcon,
  Draw as SignatureIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  DirectionsCar as CarIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import ConditionalLogicBuilder from './ConditionalLogicBuilder';
import UserAgreementsManager from './UserAgreementsManager';
import {
  Campaign as CampaignIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { locationLabel } from '../../utils/terminology';

const CHECK_TYPE_TAGS = ['safety', 'loan', 'delivery', 'location'] as const;

type QuestionType = 'text' | 'multiple_choice' | 'yes_no' | 'yes_no_na' | 'number' | 'date' | 'signature' | 'text_header' | 'email_check' | 'image_type';

interface Question {
  question_id?: string;
  question_text: string;
  question_type: QuestionType;
  is_required: boolean;
  image_required?: boolean;
  force_image_on_yes?: boolean;
  force_image_on_no?: boolean;
  hide_on_return_if_yes?: boolean;
  hide_on_return_if_no?: boolean;
  hide_on_return_if_na?: boolean;
  show_conditions?: any;
  options?: string[];
  allow_multiple_selections?: boolean;
  min_selections?: number;
  max_selections?: number;
  allow_video_attachment?: boolean;
}

interface ModernTemplateBuilderProps {
  templateName: string;
  setTemplateName: (name: string) => void;
  templateDescription: string;
  setTemplateDescription: (desc: string) => void;
  templateTags: string[];
  setTemplateTags: (tags: string[]) => void;
  questions: Question[];
  setQuestions: (questions: Question[]) => void;
  selectedAssetTypeIds: string[];
  setSelectedAssetTypeIds: (ids: string[]) => void;
  assetTypes: any[];
  forceConditionImages: boolean;
  setForceConditionImages: (force: boolean) => void;
  minConditionImages: number;
  setMinConditionImages: (min: number) => void;
  allowSkipDvla: boolean;
  setAllowSkipDvla: (allow: boolean) => void;
  allowEmailCompletion: boolean;
  setAllowEmailCompletion: (allow: boolean) => void;
  requiresSection68Waiver?: boolean;
  setRequiresSection68Waiver?: (v: boolean) => void;
  section68WaiverText?: string;
  setSection68WaiverText?: (v: string) => void;
  requiresWholeGoodNumber?: boolean;
  setRequiresWholeGoodNumber?: (v: boolean) => void;
  wholeGoodNumberPrefix?: string;
  setWholeGoodNumberPrefix?: (v: string) => void;
  suggestedFrequencyEnabled?: boolean;
  setSuggestedFrequencyEnabled?: (v: boolean) => void;
  suggestedFrequencyByAssetType?: Record<string, { value: number; unit: string }>;
  setSuggestedFrequencyByAssetType?: (v: Record<string, { value: number; unit: string }>) => void;
  onSave: () => void;
  onCancel: () => void;
  isEditing?: boolean;
  templateId?: string;
  businessPermissions?: {
    allowSafetyChecks: boolean;
    allowLoanChecks: boolean;
    allowDeliveryChecks: boolean;
    allowLocationChecks?: boolean;
    useSiteTerminology?: boolean;
    allowFrequencyScheduling?: boolean;
  };
  draftAgreements?: any[];
  setDraftAgreements?: (agreements: any[]) => void;
  draftSchedules?: any[];
  setDraftSchedules?: (schedules: any[]) => void;
  /** When set to "location", locks the builder to site/location check mode. */
  filterTag?: string | null;
}

const questionTypeIcons: Record<string, React.ReactElement> = {
  text: <TextIcon />,
  yes_no: <RadioIcon />,
  yes_no_na: <RadioIcon />,
  multiple_choice: <CheckboxIcon />,
  date: <DateIcon />,
  number: <NumberIcon />,
  signature: <SignatureIcon />,
  text_header: <TextIcon />,
  email_check: <EmailIcon />,
  image_type: <ImageIcon />,
};

const questionTypeLabels: Record<string, string> = {
  text: 'Text Input',
  yes_no: 'Yes/No',
  yes_no_na: 'Yes/No/N/A',
  multiple_choice: 'Multiple Choice',
  date: 'Date',
  number: 'Number',
  signature: 'Signature',
  text_header: 'Section Header',
  email_check: 'Email Check (Auto-send PDF)',
  image_type: 'Image (Photo Only)',
};

export const ModernTemplateBuilder: React.FC<ModernTemplateBuilderProps> = ({
  templateName,
  setTemplateName,
  templateDescription,
  setTemplateDescription,
  templateTags,
  setTemplateTags,
  questions,
  setQuestions,
  selectedAssetTypeIds,
  setSelectedAssetTypeIds,
  assetTypes,
  forceConditionImages,
  templateId,
  setForceConditionImages,
  minConditionImages,
  setMinConditionImages,
  allowSkipDvla,
  setAllowSkipDvla,
  allowEmailCompletion,
  setAllowEmailCompletion,
  requiresSection68Waiver = false,
  setRequiresSection68Waiver,
  section68WaiverText = '',
  setSection68WaiverText,
  requiresWholeGoodNumber = false,
  setRequiresWholeGoodNumber,
  wholeGoodNumberPrefix = 'WGS',
  setWholeGoodNumberPrefix,
  suggestedFrequencyEnabled = false,
  setSuggestedFrequencyEnabled,
  suggestedFrequencyByAssetType = {},
  setSuggestedFrequencyByAssetType,
  onSave,
  onCancel,
  isEditing = false,
  businessPermissions = {
    allowSafetyChecks: true,
    allowLoanChecks: true,
    allowDeliveryChecks: true,
    allowLocationChecks: false,
    useSiteTerminology: false,
    allowFrequencyScheduling: false,
  },
  draftAgreements = [],
  setDraftAgreements,
  draftSchedules = [],
  setDraftSchedules,
  filterTag = null,
}) => {
  const theme = useTheme();
  const isLocationTemplate =
    businessPermissions.allowLocationChecks === true && templateTags.includes('location');
  const isLocationContext = isLocationTemplate || filterTag === 'location';
  const siteTerm = locationLabel(!!businessPermissions.useSiteTerminology, { lowercase: true });
  const siteTermPlural = locationLabel(!!businessPermissions.useSiteTerminology, { plural: true, lowercase: true });
  const [activeTab, setActiveTab] = useState(0);
  const [expandedQuestions, setExpandedQuestions] = useState<Set<number>>(new Set());
  const [previewMode, setPreviewMode] = useState(false);
  const [editingQuestionIndex, setEditingQuestionIndex] = useState<number | null>(null);
  const [conditionalFirstDialogOpen, setConditionalFirstDialogOpen] = useState(false);

  const onSaveRef = useRef(onSave);

  useEffect(() => {
    onSaveRef.current = onSave;
  }, [onSave]);

  const [newQuestion, setNewQuestion] = useState<Question>({
    question_text: '',
    question_type: 'yes_no_na',
    is_required: true,
    image_required: false,
    force_image_on_yes: false,
    force_image_on_no: false,
    hide_on_return_if_yes: false,
    hide_on_return_if_no: false,
    hide_on_return_if_na: false,
    options: [],
    allow_multiple_selections: false,
    allow_video_attachment: false,
  });

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;
    const reorderedItem = questions[sourceIndex];

    // Conditional questions cannot be first - they depend on other questions
    if (destIndex === 0 && reorderedItem?.show_conditions) {
      setConditionalFirstDialogOpen(true);
      return;
    }

    const items = Array.from(questions);
    const [removed] = items.splice(sourceIndex, 1);
    items.splice(destIndex, 0, removed);

    setQuestions(items);
  };

  const toggleQuestionExpanded = (index: number) => {
    const newExpanded = new Set(expandedQuestions);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedQuestions(newExpanded);
  };

  const resetNewQuestion = () => ({
    question_text: '',
    question_type: 'yes_no_na' as QuestionType,
    is_required: true,
    image_required: false,
    force_image_on_yes: false,
    force_image_on_no: false,
    hide_on_return_if_yes: false,
    hide_on_return_if_no: false,
    hide_on_return_if_na: false,
    options: [],
    allow_multiple_selections: false,
    allow_video_attachment: false,
  });

  // Auto-commit helper: applies newQuestion edits to the given questions array at editIdx, returns updated array.
  const applyEditToQuestions = (qs: Question[], editIdx: number): Question[] => {
    const trimmedText = newQuestion.question_text.trim();
    if (!trimmedText) return qs; // discard empty edit

    const isDuplicate = qs.some((q, i) =>
      i !== editIdx && q.question_text.trim().toLowerCase() === trimmedText.toLowerCase()
    );
    if (isDuplicate) return qs; // discard duplicate edit silently

    const updated = [...qs];
    let updates: Partial<Question> = { ...newQuestion };

    // Multiple choice: clean options
    if (newQuestion.question_type === 'multiple_choice') {
      const opts = (newQuestion.options || []).filter(Boolean);
      if (opts.length < 2) return qs; // invalid MC, discard
      updates = { ...updates, options: opts };
    }

    // Clear MC fields when switching away from multiple_choice
    const prev = updated[editIdx];
    if (prev.question_type === 'multiple_choice' && newQuestion.question_type !== 'multiple_choice') {
      updates.options = [];
      updates.allow_multiple_selections = false;
      updates.min_selections = undefined;
      updates.max_selections = undefined;
    }

    updated[editIdx] = { ...updated[editIdx], ...updates };
    return updated;
  };

  const startEditQuestion = (index: number) => {
    // Auto-commit any active edit before switching to another question
    if (editingQuestionIndex !== null && editingQuestionIndex !== index) {
      const committed = applyEditToQuestions(questions, editingQuestionIndex);
      // Only call setQuestions if something actually changed
      if (committed !== questions) {
        setQuestions(committed);
      }
    }

    const q = questions[index];
    setNewQuestion({
      question_text: q.question_text,
      question_type: q.question_type,
      is_required: q.is_required,
      image_required: q.image_required || false,
      force_image_on_yes: q.force_image_on_yes || false,
      force_image_on_no: q.force_image_on_no || false,
      hide_on_return_if_yes: q.hide_on_return_if_yes || false,
      hide_on_return_if_no: q.hide_on_return_if_no || false,
      hide_on_return_if_na: q.hide_on_return_if_na || false,
      options: q.question_type === 'multiple_choice' ? [...(q.options || [])] : [],
      allow_multiple_selections: q.question_type === 'multiple_choice' ? (q.allow_multiple_selections || false) : false,
      min_selections: q.min_selections,
      max_selections: q.max_selections,
      show_conditions: q.show_conditions,
      allow_video_attachment: q.allow_video_attachment || false,
    });
    setEditingQuestionIndex(index);
    setExpandedQuestions(new Set()); // Collapse any expanded cards when editing
  };

  const cancelEdit = () => {
    setNewQuestion(resetNewQuestion());
    setEditingQuestionIndex(null);
  };

  // Explicitly commits the question currently being edited and returns the form
  // to its neutral "Add New Question" state — without closing the whole template.
  const saveCurrentEdit = () => {
    if (editingQuestionIndex === null) return;

    const trimmedText = newQuestion.question_text.trim();
    if (!trimmedText) {
      alert('⚠️ Question text cannot be empty.');
      return;
    }

    const isDuplicate = questions.some((q, i) =>
      i !== editingQuestionIndex && q.question_text.trim().toLowerCase() === trimmedText.toLowerCase()
    );
    if (isDuplicate) {
      alert('⚠️ A question with this text already exists in the template. Please use a unique question text.');
      return;
    }

    if (newQuestion.question_type === 'multiple_choice') {
      const opts = (newQuestion.options || []).filter(Boolean);
      if (opts.length < 2) {
        alert('⚠️ Multiple choice questions require at least 2 options.');
        return;
      }
    }

    const updated = applyEditToQuestions(questions, editingQuestionIndex);
    setQuestions(updated);
    setNewQuestion(resetNewQuestion());
    setEditingQuestionIndex(null);
  };

  // Handles the final save action. Auto-commits any open edit or pending new question,
  // updates questions state, then fires onSave after the state update has been applied.
  const handleDone = () => {
    let finalQuestions = [...questions];

    if (editingQuestionIndex !== null) {
      // Auto-commit current edit
      finalQuestions = applyEditToQuestions(finalQuestions, editingQuestionIndex);
    } else if (newQuestion.question_text.trim()) {
      // Auto-add a question the user typed but forgot to click "Add Question"
      const trimmedText = newQuestion.question_text.trim();
      const isDuplicate = finalQuestions.some(q =>
        q.question_text.trim().toLowerCase() === trimmedText.toLowerCase()
      );
      if (!isDuplicate) {
        let questionToAdd = { ...newQuestion };
        if (newQuestion.question_type === 'multiple_choice') {
          const opts = (newQuestion.options || []).filter(Boolean);
          // Only add if MC has at least 2 options; otherwise skip silently
          if (opts.length >= 2) {
            questionToAdd = { ...questionToAdd, options: opts };
          } else {
            // Skip invalid MC question
            questionToAdd = null as any;
          }
        }
        if (questionToAdd) {
          finalQuestions = [...finalQuestions, questionToAdd];
        }
      }
    }

    setQuestions(finalQuestions);
    setNewQuestion(resetNewQuestion());
    setEditingQuestionIndex(null);

    // Schedule onSave after React has committed the state updates above.
    // Using setTimeout(0) ensures the parent re-renders with the new questions
    // before handleCreateTemplate / handleUpdateTemplate reads from its closure.
    setTimeout(() => onSaveRef.current?.(), 0);
  };

  const addQuestion = () => {
    if (!newQuestion.question_text.trim()) return;

    // Validate multiple choice has at least 2 options; use cleaned options when adding
    let questionToAdd = { ...newQuestion };
    if (newQuestion.question_type === 'multiple_choice') {
      const opts = (newQuestion.options || []).filter(Boolean);
      if (opts.length < 2) {
        alert('⚠️ Multiple choice questions require at least 2 options.');
        return;
      }
      questionToAdd = { ...questionToAdd, options: opts };
    }

    // Check for duplicate question text
    const trimmedText = questionToAdd.question_text.trim().toLowerCase();
    const isDuplicate = questions.some(q => 
      q.question_text.trim().toLowerCase() === trimmedText
    );

    if (isDuplicate) {
      alert('⚠️ A question with this text already exists in the template. Please use a unique question text.');
      return;
    }

    // Debug: Log new question with conditions
    if (questionToAdd.show_conditions) {
      console.log('🎯 Adding new question with conditional logic:', {
        question: questionToAdd.question_text.substring(0, 30),
        show_conditions: questionToAdd.show_conditions
      });
    }

    setQuestions([...questions, questionToAdd]);
    setNewQuestion(resetNewQuestion());
  };

  const duplicateQuestion = (index: number) => {
    // Auto-commit any active edit first
    let base = questions;
    if (editingQuestionIndex !== null && editingQuestionIndex !== index) {
      base = applyEditToQuestions(questions, editingQuestionIndex);
      setEditingQuestionIndex(null);
      setNewQuestion(resetNewQuestion());
    }
    const questionToDuplicate = { ...base[index] };
    questionToDuplicate.question_text = `${questionToDuplicate.question_text} (Copy)`;
    const newQuestions = [...base];
    newQuestions.splice(index + 1, 0, questionToDuplicate);
    setQuestions(newQuestions);
  };

  const deleteQuestion = (index: number) => {
    // Auto-commit any active edit first (unless deleting the question being edited)
    let base = questions;
    if (editingQuestionIndex !== null && editingQuestionIndex !== index) {
      base = applyEditToQuestions(questions, editingQuestionIndex);
    }
    if (editingQuestionIndex !== null) {
      setEditingQuestionIndex(null);
      setNewQuestion(resetNewQuestion());
    }
    setQuestions(base.filter((_, i) => i !== index));
  };

  const updateQuestion = (index: number, updates: Partial<Question>) => {
    // If updating question_text, check for duplicates
    if (updates.question_text !== undefined) {
      const trimmedText = updates.question_text.trim().toLowerCase();
      const isDuplicate = questions.some((q, idx) => 
        idx !== index && q.question_text.trim().toLowerCase() === trimmedText
      );

      if (isDuplicate) {
        alert('⚠️ A question with this text already exists in the template. Please use a unique question text.');
        return;
      }
    }

    const newQuestions = [...questions];
    newQuestions[index] = { ...newQuestions[index], ...updates };
    
    // Debug: Log when show_conditions is updated
    if (updates.show_conditions !== undefined) {
      console.log('🎯 Question updated with conditional logic:', {
        index,
        question: newQuestions[index].question_text.substring(0, 30),
        show_conditions: updates.show_conditions
      });
    }
    
    setQuestions(newQuestions);
  };

  // Tab 1: Basic Information
  const renderBasicInfoTab = () => (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        {/* Template Name */}
        <TextField
          fullWidth
          label="Template Name"
          value={templateName}
          onChange={(e) => setTemplateName(e.target.value)}
          required
          placeholder="e.g., Forklift Daily Inspection"
          variant="outlined"
          InputProps={{
            sx: {
              fontSize: '1.1rem',
              fontWeight: 500,
            },
          }}
        />

        {/* Description */}
        <TextField
          fullWidth
          label="Description"
          value={templateDescription}
          onChange={(e) => setTemplateDescription(e.target.value)}
          multiline
          rows={3}
          placeholder="Describe what this template is used for..."
          variant="outlined"
        />

        {/* Check Type Selection */}
        {isLocationContext ? (
          <Alert severity="info" icon={false}>
            <Typography variant="body2">
              <strong>{locationLabel(!!businessPermissions.useSiteTerminology)} check template</strong> — runs against your{' '}
              {siteTermPlural}, not assets. Users pick a {siteTerm} on mobile or you assign one from Assign Checks.
            </Typography>
          </Alert>
        ) : (
        <Card variant="outlined" sx={{ bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
          <CardContent>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Check Type *
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
              Select one check type. Templates without a specific type default to Safety.
            </Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap">
              {businessPermissions.allowSafetyChecks && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={
                        templateTags.includes('safety') ||
                        (!templateTags.includes('loan') &&
                          !templateTags.includes('delivery') &&
                          !templateTags.includes('location'))
                      }
                      onChange={(e) => {
                        if (e.target.checked) {
                          setTemplateTags(['safety']);
                        } else {
                          setTemplateTags([]);
                        }
                      }}
                    />
                  }
                  label="Safety Check"
                />
              )}
              {businessPermissions.allowLoanChecks && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={templateTags.includes('loan')}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setTemplateTags(['loan']);
                        } else {
                          setTemplateTags([]);
                        }
                      }}
                    />
                  }
                  label="Loan Check"
                />
              )}
              {businessPermissions.allowDeliveryChecks && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={templateTags.includes('delivery')}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setTemplateTags(['delivery']);
                        } else {
                          setTemplateTags([]);
                        }
                      }}
                    />
                  }
                  label="Delivery Check"
                />
              )}
              {businessPermissions.allowLocationChecks && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={templateTags.includes('location')}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setTemplateTags(['location']);
                          setSelectedAssetTypeIds([]);
                        } else {
                          setTemplateTags([]);
                        }
                      }}
                    />
                  }
                  label={`${locationLabel(!!businessPermissions.useSiteTerminology)} Check`}
                />
              )}
            </Stack>
          </CardContent>
        </Card>
        )}

        {/* Delivery-specific: Section 6(8) Waiver */}
        {templateTags.includes('delivery') && (
          <Card variant="outlined" sx={{ bgcolor: alpha(theme.palette.warning.main, 0.04), borderColor: alpha(theme.palette.warning.main, 0.5) }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Require Section 6(8) Waiver
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    When enabled, the recipient must sign a Health and Safety at Work etc. Act 1974 Section 6(8) written undertaking before the delivery check can be completed.
                  </Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={requiresSection68Waiver}
                      onChange={(e) => setRequiresSection68Waiver && setRequiresSection68Waiver(e.target.checked)}
                    />
                  }
                  label=""
                  labelPlacement="start"
                  sx={{ ml: 1 }}
                />
              </Stack>
              {requiresSection68Waiver && (
                <Box sx={{ mt: 2 }}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={4}
                    maxRows={12}
                    label="Waiver text override (optional)"
                    placeholder="Leave blank to use the default Section 6(8) wording shown to the recipient."
                    value={section68WaiverText}
                    onChange={(e) => setSection68WaiverText && setSection68WaiverText(e.target.value)}
                    helperText="The exact wording shown at sign time is snapshotted on each signed waiver, so editing this later won't change past records."
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        {/* Delivery-specific: Wholegood number prefix */}
        {templateTags.includes('delivery') && (
          <Card variant="outlined" sx={{ bgcolor: alpha(theme.palette.info.main, 0.04), borderColor: alpha(theme.palette.info.main, 0.35) }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Require Wholegood Number
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    When enabled, users enter the number part only. Optionally add an extra part after WGS — e.g. extra prefix <strong>DEL</strong> produces <strong>WGSDEL9293</strong>. Leave blank for standard <strong>WGS9293</strong>.
                  </Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={requiresWholeGoodNumber}
                      onChange={(e) => setRequiresWholeGoodNumber && setRequiresWholeGoodNumber(e.target.checked)}
                    />
                  }
                  label=""
                  labelPlacement="start"
                  sx={{ ml: 1 }}
                />
              </Stack>
              {requiresWholeGoodNumber && (
                <TextField
                  fullWidth
                  label="Extra prefix (after WGS)"
                  placeholder="DEL"
                  value={wholeGoodNumberPrefix}
                  onChange={(e) => setWholeGoodNumberPrefix && setWholeGoodNumberPrefix(e.target.value.toUpperCase())}
                  helperText="Optional extra part after WGS. e.g. DEL → WGSDEL9293. Leave blank for WGS9293."
                  inputProps={{ maxLength: 20, style: { textTransform: 'uppercase' } }}
                  sx={{ mt: 1 }}
                />
              )}
            </CardContent>
          </Card>
        )}

        {/* Asset Types — not used for location/site checks */}
        {!isLocationContext && (
          <FormControl fullWidth>
            <Autocomplete
              multiple
              options={assetTypes}
              getOptionLabel={(option) => option.type_name}
              value={assetTypes.filter(type => selectedAssetTypeIds.includes(type.asset_type_id))}
              onChange={(_, newValue) => {
                setSelectedAssetTypeIds(newValue.map(item => item.asset_type_id));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Asset Types"
                  placeholder="Select applicable asset types"
                  helperText="Choose which asset types this template applies to"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, ...chipProps } = getTagProps({ index });
                  return (
                    <Chip
                      key={key}
                      label={option.type_name}
                      {...chipProps}
                      color="primary"
                      variant="outlined"
                    />
                  );
                })
              }
            />
          </FormControl>
        )}

        {/* Tags */}
        <Autocomplete
          multiple
          freeSolo
          options={['maintenance', 'inspection', 'training', 'compliance']}
          value={templateTags.filter((tag) => !CHECK_TYPE_TAGS.includes(tag.toLowerCase() as typeof CHECK_TYPE_TAGS[number]))}
          onChange={(_, newValue) => {
            const checkTypeTag = templateTags.find((tag) =>
              CHECK_TYPE_TAGS.includes(tag.toLowerCase() as typeof CHECK_TYPE_TAGS[number])
            );
            const organizationalTags = newValue
              .map((tag) => String(tag).trim())
              .filter((tag) => tag && !CHECK_TYPE_TAGS.includes(tag.toLowerCase() as typeof CHECK_TYPE_TAGS[number]));
            setTemplateTags(checkTypeTag ? [checkTypeTag, ...organizationalTags] : organizationalTags);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Tags"
              placeholder="Add tags for organization"
              helperText="Tags help categorize and filter templates"
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => {
              const { key, ...chipProps } = getTagProps({ index });
              return (
                <Chip
                  key={key}
                  label={option}
                  {...chipProps}
                  size="small"
                  sx={{ borderRadius: 1 }}
                />
              );
            })
          }
        />
      </Stack>
    </Box>
  );

  // Tab 2: Questions Builder
  const renderQuestionsTab = () => {
    // Check if current new question text is a duplicate (exclude the one we're editing)
    const isNewQuestionDuplicate = Boolean(
      newQuestion.question_text.trim() && questions.some((q, idx) => 
        idx !== editingQuestionIndex &&
        q.question_text.trim().toLowerCase() === newQuestion.question_text.trim().toLowerCase()
      )
    );

    return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        {/* Add New Question Card */}
        <Card
          variant="outlined"
          sx={{
            bgcolor: editingQuestionIndex !== null
              ? alpha(theme.palette.warning.main, 0.05)
              : alpha(theme.palette.primary.main, 0.02),
            border: `2px dashed ${editingQuestionIndex !== null ? theme.palette.warning.main : theme.palette.primary.main}`,
          }}
        >
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              {editingQuestionIndex !== null ? (
                <><EditIcon /> Edit Question #{editingQuestionIndex + 1}</>
              ) : (
                <><AddIcon /> Add New Question</>
              )}
            </Typography>
            
            <Stack spacing={2}>
              <TextField
                fullWidth
                label="Question Text"
                value={newQuestion.question_text}
                onChange={(e) => setNewQuestion({ ...newQuestion, question_text: e.target.value })}
                placeholder="Enter your question..."
                multiline
                rows={2}
                error={isNewQuestionDuplicate}
                helperText={isNewQuestionDuplicate ? '⚠️ This question text already exists in the template' : ''}
              />

              <FormControl fullWidth>
                <InputLabel>Question Type</InputLabel>
                <Select
                  value={newQuestion.question_type}
                  label="Question Type"
                  onChange={(e) => {
                    const newType = e.target.value as QuestionType;
                    const prevType = newQuestion.question_type;
                    // Text headers can never be required
                    const updates: Partial<Question> = { question_type: newType };
                    if (newType === 'text_header') {
                      updates.is_required = false;
                    }
                    // When changing FROM multiple_choice, clear options and multi-select fields
                    if (prevType === 'multiple_choice' && newType !== 'multiple_choice') {
                      updates.options = [];
                      updates.allow_multiple_selections = false;
                      updates.min_selections = undefined;
                      updates.max_selections = undefined;
                    }
                    // When changing TO multiple_choice, start with empty options if coming from non-multiple_choice
                    if (newType === 'multiple_choice' && prevType !== 'multiple_choice') {
                      updates.options = [];
                      updates.allow_multiple_selections = false;
                    }
                    setNewQuestion({ ...newQuestion, ...updates });
                  }}
                >
                  {Object.entries(questionTypeLabels).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {questionTypeIcons[value]}
                        {label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {newQuestion.question_type === 'multiple_choice' && (
                <TextField
                  fullWidth
                  label="Options (comma-separated)"
                  placeholder="Option 1, Option 2, Option 3"
                  helperText="Enter options separated by commas"
                  value={newQuestion.options?.join(', ') || ''}
                  onChange={(e) => {
                    // Keep empty strings so trailing comma is visible; validation filters on save
                    const options = e.target.value.split(',').map(o => o.trim());
                    setNewQuestion({ ...newQuestion, options });
                  }}
                />
              )}

              <Stack direction="row" spacing={2} flexWrap="wrap">
                {/* Hide Required for text_header - they can't be answered */}
                {newQuestion.question_type !== 'text_header' && (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={newQuestion.is_required}
                        onChange={(e) => setNewQuestion({ ...newQuestion, is_required: e.target.checked })}
                      />
                    }
                    label="Required"
                  />
                )}
                {/* Hide Image Required for yes/no, text_header, and image_type (image_type is photo-only by definition) */}
                {newQuestion.question_type !== 'yes_no' && 
                 newQuestion.question_type !== 'yes_no_na' && 
                 newQuestion.question_type !== 'text_header' &&
                 newQuestion.question_type !== 'image_type' && (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={newQuestion.image_required || false}
                        onChange={(e) => setNewQuestion({ ...newQuestion, image_required: e.target.checked })}
                      />
                    }
                    label="Image Required"
                  />
                )}
                {/* Allow video attachment — available for all question types except text_header */}
                {newQuestion.question_type !== 'text_header' && (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={newQuestion.allow_video_attachment || false}
                        onChange={(e) => setNewQuestion({ ...newQuestion, allow_video_attachment: e.target.checked })}
                      />
                    }
                    label="Allow video attachment (max 2 minutes)"
                  />
                )}
              </Stack>

              {/* Advanced Options for Yes/No Questions */}
              {(newQuestion.question_type === 'yes_no' || newQuestion.question_type === 'yes_no_na') && (
                <Card variant="outlined" sx={{ bgcolor: alpha(theme.palette.grey[500], 0.03) }}>
                  <CardContent>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
                      Advanced Options
                    </Typography>
                    
                    <Stack spacing={2}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                          Image Requirements
                        </Typography>
                        <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ pl: 1 }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={newQuestion.force_image_on_yes || false}
                                onChange={(e) => setNewQuestion({ ...newQuestion, force_image_on_yes: e.target.checked })}
                                size="small"
                              />
                            }
                            label="Require image if Yes"
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={newQuestion.force_image_on_no || false}
                                onChange={(e) => setNewQuestion({ ...newQuestion, force_image_on_no: e.target.checked })}
                                size="small"
                              />
                            }
                            label="Require image if No"
                          />
                        </Stack>
                      </Box>
                      
                      {/* Return Check Behavior - Only show for Loan templates */}
                      {templateTags.includes('loan') && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                            Return Check Behavior
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontStyle: 'italic' }}>
                            Controls whether this question appears on loan return checks
                          </Typography>
                          <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ pl: 1 }}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={newQuestion.hide_on_return_if_yes || false}
                                  onChange={(e) => setNewQuestion({ ...newQuestion, hide_on_return_if_yes: e.target.checked })}
                                  size="small"
                                />
                              }
                              label="Hide on return if Yes"
                            />
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={newQuestion.hide_on_return_if_no || false}
                                  onChange={(e) => setNewQuestion({ ...newQuestion, hide_on_return_if_no: e.target.checked })}
                                  size="small"
                                />
                              }
                              label="Hide on return if No"
                            />
                            {newQuestion.question_type === 'yes_no_na' && (
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={newQuestion.hide_on_return_if_na || false}
                                    onChange={(e) => setNewQuestion({ ...newQuestion, hide_on_return_if_na: e.target.checked })}
                                    size="small"
                                  />
                                }
                                label="Hide on return if N/A"
                              />
                            )}
                          </Stack>
                        </Box>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              )}

              {/* Advanced Options for Multiple Choice */}
              {newQuestion.question_type === 'multiple_choice' && (
                <Card variant="outlined" sx={{ bgcolor: alpha(theme.palette.grey[500], 0.03) }}>
                  <CardContent>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
                      Advanced Options
                    </Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={newQuestion.allow_multiple_selections || false}
                          onChange={(e) => setNewQuestion({ 
                            ...newQuestion, 
                            allow_multiple_selections: e.target.checked,
                            min_selections: e.target.checked ? newQuestion.min_selections : undefined,
                            max_selections: e.target.checked ? newQuestion.max_selections : undefined
                          })}
                          size="small"
                        />
                      }
                      label="Allow multiple selections"
                    />
                    {newQuestion.allow_multiple_selections && (
                      <Box sx={{ mt: 2, ml: 4, display: 'flex', gap: 2 }}>
                        <TextField
                          label="Min Selections"
                          type="number"
                          size="small"
                          value={newQuestion.min_selections || ''}
                          onChange={(e) => setNewQuestion({ 
                            ...newQuestion, 
                            min_selections: e.target.value ? parseInt(e.target.value) : undefined 
                          })}
                          InputProps={{ inputProps: { min: 1 } }}
                          helperText="Minimum required selections"
                          sx={{ flex: 1 }}
                        />
                        <TextField
                          label="Max Selections"
                          type="number"
                          size="small"
                          value={newQuestion.max_selections || ''}
                          onChange={(e) => setNewQuestion({ 
                            ...newQuestion, 
                            max_selections: e.target.value ? parseInt(e.target.value) : undefined 
                          })}
                          InputProps={{ inputProps: { min: newQuestion.min_selections || 1 } }}
                          helperText="Maximum allowed selections"
                          sx={{ flex: 1 }}
                        />
                      </Box>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Conditional Logic */}
              <ConditionalLogicBuilder
                currentQuestionIndex={editingQuestionIndex !== null ? editingQuestionIndex : questions.length}
                questions={questions}
                showConditions={newQuestion.show_conditions}
                onChange={(conditions) => setNewQuestion({ ...newQuestion, show_conditions: conditions })}
              />
            </Stack>
          </CardContent>
          <CardActions sx={{ justifyContent: 'space-between', alignItems: 'center', px: 2, pb: 2, pt: 0 }}>
            {editingQuestionIndex !== null ? (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  Changes save automatically if you switch to another question
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button variant="outlined" color="inherit" onClick={cancelEdit} size="small">
                    Discard changes
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={saveCurrentEdit}
                    disabled={!newQuestion.question_text.trim() || isNewQuestionDuplicate}
                    size="small"
                    startIcon={<CheckIcon />}
                  >
                    Save Question
                  </Button>
                </Stack>
              </>
            ) : (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={addQuestion}
                disabled={!newQuestion.question_text.trim() || isNewQuestionDuplicate}
                size="large"
                fullWidth
              >
                Add Question
              </Button>
            )}
          </CardActions>
        </Card>

        {/* Questions List with Drag and Drop */}
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Questions ({questions.length})
            </Typography>
            <Button
              size="small"
              startIcon={previewMode ? <EditIcon /> : <PreviewIcon />}
              onClick={() => setPreviewMode(!previewMode)}
            >
              {previewMode ? 'Edit Mode' : 'Preview Mode'}
            </Button>
          </Box>

          {questions.length === 0 ? (
            <Alert severity="info" icon={<InfoIcon />}>
              No questions added yet. Use the form above to add your first question.
            </Alert>
          ) : (
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="questions">
                {(provided) => (
                  <Stack
                    spacing={2}
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                  >
                    {questions.map((question, index) => (
                      <Draggable
                        key={`question-${index}`}
                        draggableId={`question-${index}`}
                        index={index}
                        isDragDisabled={previewMode}
                      >
                        {(provided, snapshot) => (
                          <Card
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            variant="outlined"
                            sx={{
                              bgcolor: snapshot.isDragging ? alpha(theme.palette.primary.main, 0.1) : 'background.paper',
                              transform: snapshot.isDragging ? 'rotate(2deg)' : 'none',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                boxShadow: theme.shadows[4],
                              },
                            }}
                          >
                            <CardContent>
                              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                                {!previewMode && (
                                  <Box
                                    {...provided.dragHandleProps}
                                    sx={{
                                      cursor: 'grab',
                                      color: 'text.secondary',
                                      '&:active': { cursor: 'grabbing' },
                                    }}
                                  >
                                    <DragIcon />
                                  </Box>
                                )}

                                <Box sx={{ flex: 1 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <Chip
                                      label={`#${index + 1}`}
                                      size="small"
                                      color="primary"
                                      variant="outlined"
                                    />
                                    {questionTypeIcons[question.question_type]}
                                    <Typography variant="caption" color="text.secondary">
                                      {questionTypeLabels[question.question_type]}
                                    </Typography>
                                    {question.is_required && (
                                      <Chip label="Required" size="small" color="error" />
                                    )}
                                    {(question.image_required || question.question_type === 'image_type') && (
                                      <Chip
                                        icon={<ImageIcon />}
                                        label="Image"
                                        size="small"
                                        color="info"
                                      />
                                    )}
                                    {question.allow_video_attachment && (
                                      <Chip
                                        label="Video"
                                        size="small"
                                        sx={{ bgcolor: '#dcfce7', color: '#15803d', border: '1px solid #86efac' }}
                                      />
                                    )}
                                    {question.show_conditions && (
                                      <Chip
                                        label="Conditional"
                                        size="small"
                                        color="warning"
                                        variant="outlined"
                                      />
                                    )}
                                  </Box>

                                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                    {question.question_text}
                                  </Typography>

                                  {question.options && question.options.length > 0 && (
                                    <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                      {question.options.map((option, optIndex) => (
                                        <Chip
                                          key={optIndex}
                                          label={option}
                                          size="small"
                                          variant="outlined"
                                        />
                                      ))}
                                    </Box>
                                  )}

                                  {/* Expandable Advanced Options */}
                                  <Collapse in={expandedQuestions.has(index)}>
                                    <Box sx={{ mt: 2, p: 2, bgcolor: alpha(theme.palette.grey[500], 0.05), borderRadius: 1 }}>
                                      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
                                        Advanced Options
                                      </Typography>
                                      <Stack spacing={1.5}>
                                        {(question.question_type === 'yes_no' || question.question_type === 'yes_no_na') && (
                                          <>
                                            <Box>
                                              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                                Image Requirements
                                              </Typography>
                                              <Stack direction="row" spacing={2} sx={{ pl: 1 }}>
                                                <FormControlLabel
                                                  control={
                                                    <Checkbox
                                                      checked={question.force_image_on_yes || false}
                                                      onChange={(e) => updateQuestion(index, { force_image_on_yes: e.target.checked })}
                                                      size="small"
                                                    />
                                                  }
                                                  label="Require image if Yes"
                                                />
                                                <FormControlLabel
                                                  control={
                                                    <Checkbox
                                                      checked={question.force_image_on_no || false}
                                                      onChange={(e) => updateQuestion(index, { force_image_on_no: e.target.checked })}
                                                      size="small"
                                                    />
                                                  }
                                                  label="Require image if No"
                                                />
                                              </Stack>
                                            </Box>
                                            
                                            {/* Return Check Behavior - Only show for Loan templates */}
                                            {templateTags.includes('loan') && (
                                              <Box>
                                                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                                  Return Check Behavior
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', fontStyle: 'italic' }}>
                                                  Controls whether this question appears on loan return checks
                                                </Typography>
                                                <Stack direction="row" spacing={2} sx={{ pl: 1 }}>
                                                  <FormControlLabel
                                                    control={
                                                      <Checkbox
                                                        checked={question.hide_on_return_if_yes || false}
                                                        onChange={(e) => updateQuestion(index, { hide_on_return_if_yes: e.target.checked })}
                                                        size="small"
                                                      />
                                                    }
                                                    label="Hide on return if Yes"
                                                  />
                                                    <FormControlLabel
                                                    control={
                                                      <Checkbox
                                                        checked={question.hide_on_return_if_no || false}
                                                        onChange={(e) => updateQuestion(index, { hide_on_return_if_no: e.target.checked })}
                                                        size="small"
                                                      />
                                                    }
                                                    label="Hide on return if No"
                                                  />
                                                  {question.question_type === 'yes_no_na' && (
                                                    <FormControlLabel
                                                      control={
                                                        <Checkbox
                                                          checked={question.hide_on_return_if_na || false}
                                                          onChange={(e) => updateQuestion(index, { hide_on_return_if_na: e.target.checked })}
                                                          size="small"
                                                        />
                                                      }
                                                      label="Hide on return if N/A"
                                                    />
                                                  )}
                                                </Stack>
                                              </Box>
                                            )}
                                          </>
                                        )}
                                        {question.question_type === 'multiple_choice' && (
                                          <>
                                            <FormControlLabel
                                              control={
                                                <Checkbox
                                                  checked={question.allow_multiple_selections || false}
                                                  onChange={(e) => updateQuestion(index, { 
                                                    allow_multiple_selections: e.target.checked,
                                                    min_selections: e.target.checked ? question.min_selections : undefined,
                                                    max_selections: e.target.checked ? question.max_selections : undefined
                                                  })}
                                                  size="small"
                                                />
                                              }
                                              label="Allow multiple selections"
                                            />
                                            {question.allow_multiple_selections && (
                                              <Box sx={{ mt: 2, ml: 4, display: 'flex', gap: 2 }}>
                                                <TextField
                                                  label="Min Selections"
                                                  type="number"
                                                  size="small"
                                                  value={question.min_selections || ''}
                                                  onChange={(e) => updateQuestion(index, { 
                                                    min_selections: e.target.value ? parseInt(e.target.value) : undefined 
                                                  })}
                                                  InputProps={{ inputProps: { min: 1 } }}
                                                  helperText="Minimum required selections"
                                                  sx={{ flex: 1 }}
                                                />
                                                <TextField
                                                  label="Max Selections"
                                                  type="number"
                                                  size="small"
                                                  value={question.max_selections || ''}
                                                  onChange={(e) => updateQuestion(index, { 
                                                    max_selections: e.target.value ? parseInt(e.target.value) : undefined 
                                                  })}
                                                  InputProps={{ inputProps: { min: question.min_selections || 1 } }}
                                                  helperText="Maximum allowed selections"
                                                  sx={{ flex: 1 }}
                                                />
                                              </Box>
                                            )}
                                          </>
                                        )}
                                        {/* Allow video attachment */}
                                        {question.question_type !== 'text_header' && (
                                          <FormControlLabel
                                            control={
                                              <Checkbox
                                                checked={question.allow_video_attachment || false}
                                                onChange={(e) => updateQuestion(index, { allow_video_attachment: e.target.checked })}
                                                size="small"
                                              />
                                            }
                                            label="Allow video attachment (max 2 minutes)"
                                          />
                                        )}
                                      </Stack>

                                      {/* Conditional Logic */}
                                      <Box sx={{ mt: 2 }}>
                                        <ConditionalLogicBuilder
                                          currentQuestionIndex={index}
                                          questions={questions}
                                          showConditions={question.show_conditions}
                                          onChange={(conditions) => updateQuestion(index, { show_conditions: conditions })}
                                        />
                                      </Box>
                                    </Box>
                                  </Collapse>
                                </Box>

                                {!previewMode && (
                                  <Stack direction="row" spacing={0.5}>
                                    <Tooltip title={editingQuestionIndex === index ? 'Editing...' : 'Edit'}>
                                      <IconButton
                                        size="small"
                                        onClick={() => startEditQuestion(index)}
                                        color={editingQuestionIndex === index ? 'primary' : 'default'}
                                      >
                                        <EditIcon />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Advanced Options">
                                      <IconButton
                                        size="small"
                                        onClick={() => toggleQuestionExpanded(index)}
                                        disabled={editingQuestionIndex === index}
                                      >
                                        {expandedQuestions.has(index) ? <CollapseIcon /> : <ExpandIcon />}
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Duplicate">
                                      <IconButton
                                        size="small"
                                        onClick={() => duplicateQuestion(index)}
                                        color="primary"
                                      >
                                        <CopyIcon />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete">
                                      <IconButton
                                        size="small"
                                        onClick={() => deleteQuestion(index)}
                                        color="error"
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </Tooltip>
                                  </Stack>
                                )}
                              </Box>
                            </CardContent>
                          </Card>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </Stack>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </Box>
      </Stack>
    </Box>
    );
  };

  // Tab 3: Advanced Settings
  const renderAdvancedTab = () => {
    const isLoanTemplate = templateTags.includes('loan');

    return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        {/* Show loan-specific settings only when template is tagged as 'loan' */}
        {!isLoanTemplate && !isLocationContext && (
          <Alert severity="info" icon={<InfoIcon />}>
            <Typography variant="body2">
              Loan-specific settings (Condition Images, DVLA Check) will appear here when you tag this template as a <strong>"loan"</strong> check in the Basic Info tab.
            </Typography>
          </Alert>
        )}

        {/* Condition Images - Only for Loan Templates */}
        {isLoanTemplate && (
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <ImageIcon color="primary" />
                <Typography variant="h6">Condition Images</Typography>
              </Box>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={forceConditionImages}
                    onChange={(e) => {
                      setForceConditionImages(e.target.checked);
                      if (!e.target.checked) setMinConditionImages(0);
                    }}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="subtitle2">Force Condition Images</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Require users to take condition photos before completing
                    </Typography>
                  </Box>
                }
              />

              <Collapse in={forceConditionImages}>
                <Box sx={{ mt: 2, pl: 4 }}>
                  <TextField
                    label="Minimum Images Required"
                    type="number"
                    value={minConditionImages}
                    onChange={(e) => setMinConditionImages(Math.max(0, parseInt(e.target.value) || 0))}
                    InputProps={{ inputProps: { min: 0, max: 20 } }}
                    helperText="0 = at least 1 image, or specify exact minimum"
                    size="small"
                    sx={{ maxWidth: 300 }}
                  />
                </Box>
              </Collapse>
            </CardContent>
          </Card>
        )}

        {/* DVLA Settings - Only for Loan Templates */}
        {isLoanTemplate && (
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CarIcon color="primary" />
                <Typography variant="h6">DVLA Check Settings</Typography>
              </Box>
              
              <Alert severity="info" sx={{ mb: 2 }}>
                These settings apply to loan checks with DVLA verification requirements
              </Alert>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={allowSkipDvla}
                    onChange={(e) => setAllowSkipDvla(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="subtitle2">Allow Skip DVLA Check</Typography>
                    <Typography variant="caption" color="text.secondary">
                      When enabled, users can skip the DVLA check entirely without being sent a reminder email.
                      Use this for loan checks where DVLA verification is not required.
                    </Typography>
                  </Box>
                }
              />

              <Box sx={{ mt: 2, p: 2, bgcolor: alpha(theme.palette.info.main, 0.05), borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  <strong>Note:</strong> Without this enabled, users must either:
                  <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                    <li>Complete the DVLA check in the app, OR</li>
                    <li>Skip and receive an email to complete it later</li>
                  </ul>
                  Enabling this option adds a third choice: Skip without any follow-up.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* User Agreements - Only for Loan Templates */}
        {isLoanTemplate && (
          <Box sx={{ mt: 2 }}>
            <UserAgreementsManager
              templateId={templateId || 'draft'}
              templateName={templateName}
              assetTypeIds={selectedAssetTypeIds}
              isDraft={!templateId}
              draftAgreements={draftAgreements}
              onDraftAgreementsChange={setDraftAgreements}
            />
          </Box>
        )}

        {/* Frequency & Scheduling — managed in Communications module */}
        {templateTags.includes('safety') && !templateTags.includes('loan') && !templateTags.includes('delivery') && !templateTags.includes('location') && businessPermissions.allowFrequencyScheduling !== false && (
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <CampaignIcon color="primary" />
                <Typography variant="h6">Schedules & Alerts</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Schedule management has moved to the <strong>Communications</strong> module, where you
                can manage all schedules across templates, configure round-up emails, and set up
                per-asset notifications — all in one place.
              </Typography>
              <Button
                variant="outlined"
                startIcon={<OpenInNewIcon />}
                onClick={() => {
                  const templateParam = templateId ? `&template=${templateId}` : '';
                  window.location.href = `/admin/communications?tab=schedules${templateParam}`;
                }}
                sx={{ textTransform: 'none' }}
              >
                Manage Schedules in Communications
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Email Completion — not used for site/location checks */}
        {!isLocationContext && (
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <EmailIcon color="primary" />
                <Typography variant="h6">Email Completion</Typography>
              </Box>

              <FormControlLabel
                control={
                  <Switch
                    checked={allowEmailCompletion}
                    onChange={(e) => setAllowEmailCompletion(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="subtitle2">Allow Email Completion</Typography>
                    <Typography variant="caption" color="text.secondary">
                      When enabled, you can send this check to anyone via email. They can complete it from a link in their browser — no account or app required.
                    </Typography>
                  </Box>
                }
              />

              {allowEmailCompletion && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Once saved, a <strong>"Send via Email"</strong> button will appear on this template's card. Enter the recipient's email and an optional message to send them a one-time completion link.
                </Alert>
              )}
            </CardContent>
          </Card>
        )}

        {/* Template Statistics */}
        <Card variant="outlined" sx={{ bgcolor: alpha(theme.palette.success.main, 0.02) }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Template Summary
            </Typography>
            <Stack spacing={1.5}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography color="text.secondary">Total Questions:</Typography>
                <Chip label={questions.length} color="primary" size="small" />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography color="text.secondary">Required Questions:</Typography>
                <Chip
                  label={questions.filter(q => q.is_required).length}
                  color="error"
                  size="small"
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography color="text.secondary">Image Required:</Typography>
                <Chip
                  label={questions.filter(q => q.image_required).length}
                  color="info"
                  size="small"
                />
              </Box>
              {!isLocationContext && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography color="text.secondary">Asset Types:</Typography>
                  <Chip
                    label={selectedAssetTypeIds.length}
                    color="success"
                    size="small"
                  />
                </Box>
              )}
            </Stack>
          </CardContent>
        </Card>

        {/* Validation Warnings */}
        {questions.length === 0 && (
          <Alert severity="warning">
            This template has no questions. Add at least one question before saving.
          </Alert>
        )}
        {!templateName.trim() && (
          <Alert severity="error">
            Template name is required.
          </Alert>
        )}
      </Stack>
    </Box>
    );
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Title Bar with Close Button */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="h5" fontWeight={700}>
            {isEditing ? 'Edit Template' : '🚀 Template Builder 2.0'}
          </Typography>
          {!isEditing && (
            <Chip 
              label="NEW" 
              size="small" 
              color="primary" 
              sx={{ 
                fontWeight: 700,
                fontSize: '0.7rem',
                height: 20,
              }} 
            />
          )}
        </Box>
        <IconButton
          onClick={onCancel}
          size="large"
          sx={{
            color: 'text.secondary',
            '&:hover': {
              color: 'error.main',
              bgcolor: alpha(theme.palette.error.main, 0.1),
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Header */}
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          position: 'sticky',
          top: 0,
          zIndex: 1,
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            '& .MuiTab-root': {
              minHeight: 64,
              fontSize: '0.95rem',
              fontWeight: 500,
            },
          }}
        >
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InfoIcon />
                Basic Info
                {!templateName.trim() && (
                  <Badge color="error" variant="dot" />
                )}
              </Box>
            }
          />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckIcon />
                Questions
                <Chip label={questions.length} size="small" />
              </Box>
            }
          />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SettingsIcon />
                Advanced
              </Box>
            }
          />
        </Tabs>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        {activeTab === 0 && renderBasicInfoTab()}
        {activeTab === 1 && renderQuestionsTab()}
        {activeTab === 2 && renderAdvancedTab()}
      </Box>

      {/* Footer Actions */}
      <Box
        sx={{
          borderTop: 1,
          borderColor: 'divider',
          p: 2,
          bgcolor: 'background.paper',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
        }}
      >
        {isEditing ? (
          <>
            <Box />
            <Button
              variant="contained"
              onClick={handleDone}
              size="large"
              startIcon={<CheckIcon />}
            >
              Save & Close
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="outlined"
              onClick={onCancel}
              size="large"
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleDone}
              disabled={!templateName.trim() || (questions.length === 0 && !newQuestion.question_text.trim())}
              size="large"
              startIcon={<CheckIcon />}
            >
              Create Template
            </Button>
          </>
        )}
      </Box>

      {/* Dialog: Conditional question cannot be first */}
      <Dialog open={conditionalFirstDialogOpen} onClose={() => setConditionalFirstDialogOpen(false)}>
        <DialogTitle>Cannot Move Conditional Question to Top</DialogTitle>
        <DialogContent>
          <Typography>
            Conditional questions depend on answers from other questions, so they must appear after
            the questions they depend on. The first question in a template cannot have conditions.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConditionalFirstDialogOpen(false)} variant="contained">
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ModernTemplateBuilder;

